import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';

import Button from '../../components/ui/Button';
import InvoiceFilters from './components/InvoiceFilters';
import InvoiceTable from './components/InvoiceTable';
import InvoiceSummaryPanel from './components/InvoiceSummaryPanel';
import InvoiceModal from './components/InvoiceModal';
import PaymentRecordModal from './components/PaymentRecordModal';

const InvoiceManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // view, edit, create
  const [currentLanguage, setCurrentLanguage] = useState('es');

  // Mock invoice data
  const mockInvoices = [
    {
      id: 1,
      number: 'INV-2024-001',
      client: 'Tecnología Avanzada S.L.',
      clientEmail: 'admin@tecavanzada.es',
      clientAddress: 'Calle Mayor 123\n28001 Madrid\nEspaña',
      date: '2024-01-15',
      dueDate: '2024-02-14',
      amount: 2450.50,
      paidAmount: 2450.50,
      status: 'paid',
      items: [
        { id: 1, description: 'Desarrollo de aplicación web', quantity: 1, price: 2000, tax: 21 },
        { id: 2, description: 'Consultoría técnica', quantity: 5, price: 80, tax: 21 }
      ],
      notes: 'Pago mediante transferencia bancaria',
      paymentTerms: 'net30'
    },
    {
      id: 2,
      number: 'INV-2024-002',
      client: 'Comercial Barcelona S.A.',
      clientEmail: 'facturacion@comercialbarcelona.es',
      clientAddress: 'Passeig de Gràcia 85\n08008 Barcelona\nEspaña',
      date: '2024-01-20',
      dueDate: '2024-02-19',
      amount: 1875.75,
      paidAmount: 0,
      status: 'sent',
      items: [
        { id: 1, description: 'Sistema de gestión de inventario', quantity: 1, price: 1500, tax: 21 },
        { id: 2, description: 'Formación de usuarios', quantity: 3, price: 100, tax: 21 }
      ],
      notes: 'Incluye 3 meses de soporte técnico',
      paymentTerms: 'net30'
    },
    {
      id: 3,
      number: 'INV-2024-003',
      client: 'Industrias del Norte S.L.',
      clientEmail: 'contabilidad@industriasnorte.es',
      clientAddress: 'Polígono Industrial Norte\n48940 Leioa, Vizcaya\nEspaña',
      date: '2024-01-10',
      dueDate: '2024-01-25',
      amount: 3250.00,
      paidAmount: 1500.00,
      status: 'overdue',
      items: [
        { id: 1, description: 'Plataforma e-commerce completa', quantity: 1, price: 2500, tax: 21 },
        { id: 2, description: 'Integración con ERP', quantity: 1, price: 500, tax: 21 }
      ],
      notes: 'Pago parcial recibido. Pendiente completar.',
      paymentTerms: 'net15'
    },
    {
      id: 4,
      number: 'INV-2024-004',
      client: 'Servicios Profesionales Madrid',
      clientEmail: 'info@serviciosmadrid.com',
      clientAddress: 'Calle Alcalá 200\n28028 Madrid\nEspaña',
      date: '2024-01-25',
      dueDate: '2024-02-24',
      amount: 1320.00,
      paidAmount: 0,
      status: 'draft',
      items: [
        { id: 1, description: 'Auditoría de sistemas', quantity: 8, price: 150, tax: 10 }
      ],
      notes: 'Borrador pendiente de envío',
      paymentTerms: 'net30'
    },
    {
      id: 5,
      number: 'INV-2024-005',
      client: 'Construcciones Valencia S.A.',
      clientEmail: 'administracion@construvalencia.es',
      clientAddress: 'Avenida del Puerto 45\n46021 Valencia\nEspaña',
      date: '2024-01-12',
      dueDate: '2024-01-27',
      amount: 4125.60,
      paidAmount: 0,
      status: 'overdue',
      items: [
        { id: 1, description: 'Software de gestión de proyectos', quantity: 1, price: 3000, tax: 21 },
        { id: 2, description: 'Licencias adicionales', quantity: 5, price: 200, tax: 21 }
      ],
      notes: 'Cliente notificado del vencimiento',
      paymentTerms: 'net15'
    },
    {
      id: 6,
      number: 'INV-2024-006',
      client: 'Logística Andaluza S.L.',
      clientEmail: 'finanzas@logisticaandaluza.es',
      clientAddress: 'Polígono Sur, Nave 12\n41940 Tomares, Sevilla\nEspaña',
      date: '2024-01-28',
      dueDate: '2024-02-27',
      amount: 2750.25,
      paidAmount: 2750.25,
      status: 'paid',
      items: [
        { id: 1, description: 'Sistema de tracking GPS', quantity: 1, price: 2200, tax: 21 },
        { id: 2, description: 'Instalación y configuración', quantity: 1, price: 300, tax: 21 }
      ],
      notes: 'Pago recibido por transferencia',
      paymentTerms: 'net30'
    }
  ];

  useEffect(() => {
    // Load language preference
    const savedLanguage = localStorage.getItem('language') || 'es';
    setCurrentLanguage(savedLanguage);
    
    // Initialize invoices
    setInvoices(mockInvoices);
    setFilteredInvoices(mockInvoices);
  }, []);

  const handleFiltersChange = (filters) => {
    let filtered = [...invoices];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(invoice =>
        invoice.number.toLowerCase().includes(searchTerm) ||
        invoice.client.toLowerCase().includes(searchTerm) ||
        invoice.clientEmail.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === filters.status);
    }

    // Client filter
    if (filters.client) {
      const clientTerm = filters.client.toLowerCase();
      filtered = filtered.filter(invoice =>
        invoice.client.toLowerCase().includes(clientTerm)
      );
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(invoice => {
        const invoiceDate = new Date(invoice.date);
        
        switch (filters.dateRange) {
          case 'today':
            return invoiceDate >= today;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return invoiceDate >= weekAgo;
          case 'month':
            return invoiceDate.getMonth() === now.getMonth() && 
                   invoiceDate.getFullYear() === now.getFullYear();
          case 'quarter':
            const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
            return invoiceDate >= quarterStart;
          case 'year':
            return invoiceDate.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      });
    }

    // Amount range filter
    if (filters.amountRange !== 'all') {
      filtered = filtered.filter(invoice => {
        switch (filters.amountRange) {
          case '0-500':
            return invoice.amount >= 0 && invoice.amount <= 500;
          case '500-1000':
            return invoice.amount > 500 && invoice.amount <= 1000;
          case '1000-5000':
            return invoice.amount > 1000 && invoice.amount <= 5000;
          case '5000+':
            return invoice.amount > 5000;
          default:
            return true;
        }
      });
    }

    setFilteredInvoices(filtered);
  };

  const handleInvoiceSelect = (invoice) => {
    setSelectedInvoice(invoice);
    setModalMode('view');
    setIsInvoiceModalOpen(true);
  };

  const handleCreateInvoice = () => {
    setSelectedInvoice(null);
    setModalMode('create');
    setIsInvoiceModalOpen(true);
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setModalMode('edit');
    setIsInvoiceModalOpen(true);
  };

  const handleSaveInvoice = (invoiceData) => {
    if (modalMode === 'create') {
      const newInvoice = {
        ...invoiceData,
        id: Date.now(),
        status: 'draft',
        paidAmount: 0
      };
      setInvoices(prev => [...prev, newInvoice]);
      setFilteredInvoices(prev => [...prev, newInvoice]);
    } else if (modalMode === 'edit') {
      setInvoices(prev => prev.map(inv => 
        inv.id === invoiceData.id ? invoiceData : inv
      ));
      setFilteredInvoices(prev => prev.map(inv => 
        inv.id === invoiceData.id ? invoiceData : inv
      ));
    }
    setIsInvoiceModalOpen(false);
    setSelectedInvoice(null);
  };

  const handlePaymentRecord = (invoice) => {
    setSelectedInvoice(invoice);
    setIsPaymentModalOpen(true);
  };

  const handleSavePayment = (paymentData) => {
    const updatedInvoice = {
      ...selectedInvoice,
      paidAmount: (selectedInvoice.paidAmount || 0) + paymentData.amount,
      status: (selectedInvoice.paidAmount || 0) + paymentData.amount >= selectedInvoice.amount ? 'paid' : 'sent'
    };

    setInvoices(prev => prev.map(inv => 
      inv.id === selectedInvoice.id ? updatedInvoice : inv
    ));
    setFilteredInvoices(prev => prev.map(inv => 
      inv.id === selectedInvoice.id ? updatedInvoice : inv
    ));

    setIsPaymentModalOpen(false);
    setSelectedInvoice(null);
  };

  const handleStatusChange = (invoiceId, newStatus) => {
    setInvoices(prev => prev.map(inv => 
      inv.id === invoiceId ? { ...inv, status: newStatus } : inv
    ));
    setFilteredInvoices(prev => prev.map(inv => 
      inv.id === invoiceId ? { ...inv, status: newStatus } : inv
    ));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-3xl font-heading-bold text-text-primary mb-2">
                Gestión de Facturas
              </h1>
              <p className="text-text-secondary">
                Administra facturas, registra pagos y controla el estado de cobros
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="secondary"
                iconName="Download"
                iconSize={16}
              >
                Exportar
              </Button>
              
              <Button
                variant="primary"
                onClick={handleCreateInvoice}
                iconName="Plus"
                iconSize={16}
              >
                Nueva Factura
              </Button>
            </div>
          </div>

          {/* Filters */}
          <InvoiceFilters 
            onFiltersChange={handleFiltersChange}
            totalInvoices={filteredInvoices.length}
          />

          {/* Main Content */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            {/* Invoice Table */}
            <div className="xl:col-span-8">
              <InvoiceTable
                invoices={filteredInvoices}
                onInvoiceSelect={handleInvoiceSelect}
                onStatusChange={handleStatusChange}
                onPaymentRecord={handlePaymentRecord}
              />
            </div>

            {/* Summary Panel */}
            <div className="xl:col-span-4">
              <InvoiceSummaryPanel invoices={filteredInvoices} />
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <InvoiceModal
        invoice={selectedInvoice}
        isOpen={isInvoiceModalOpen}
        onClose={() => {
          setIsInvoiceModalOpen(false);
          setSelectedInvoice(null);
        }}
        onSave={handleSaveInvoice}
        mode={modalMode}
      />

      <PaymentRecordModal
        invoice={selectedInvoice}
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedInvoice(null);
        }}
        onSave={handleSavePayment}
      />

      {/* Quick Action FAB */}
      <QuickActionFAB />
    </div>
  );
};

export default InvoiceManagement;